import { API_URL, fetchWithHandling } from './api';

// ---------------------------------------------------------------------------
// Dashboard API Functions
//
// Chat-built dashboards (docs/DASHBOARD_BUILDER.md): a spec of widgets plus a
// 12-column layout, and the rows each widget's source returns. Responses are
// the AppResponse envelope ({ success, data }).
// ---------------------------------------------------------------------------

const base = `${API_URL}/data/dashboards`;
const headers = (accessToken) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
});
const one = (dashboardId) => `${base}/${encodeURIComponent(dashboardId)}`;

export async function listDashboards(accessToken) {
  return fetchWithHandling(`${base}/list`, {
    method: 'GET',
    headers: headers(accessToken),
  });
}

export async function getDashboard(accessToken, dashboardId) {
  return fetchWithHandling(one(dashboardId), {
    method: 'GET',
    headers: headers(accessToken),
  });
}

/** Every widget's rows after the filter bar:
 * { widgets: { [widgetId]: { rows, fetched_at, ... } | { error } },
 *   filter_options: { [filterId]: [value, ...] } }. */
export async function getDashboardData(
  accessToken,
  dashboardId,
  { refresh = false, filters = {} } = {},
) {
  const query = new URLSearchParams({ refresh: String(refresh) });
  const set = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  if (Object.keys(set).length) query.set('filters', JSON.stringify(set));
  return fetchWithHandling(`${one(dashboardId)}/data?${query}`, {
    method: 'GET',
    headers: headers(accessToken),
  });
}

// --- Saved snapshots: the viewer's own copies of the data. ---

/** Newest first: [{ id, name, filters, created_at }]. */
export async function listSnapshots(accessToken, dashboardId) {
  return fetchWithHandling(`${one(dashboardId)}/snapshots`, {
    method: 'GET',
    headers: headers(accessToken),
  });
}

export async function createSnapshot(accessToken, dashboardId, name, filters) {
  return fetchWithHandling(`${one(dashboardId)}/snapshots`, {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({ name: name || null, filters: filters || {} }),
  });
}

/** { id, name, filters, created_at, spec, widgets }. */
export async function getSnapshot(accessToken, dashboardId, snapshotId) {
  return fetchWithHandling(
    `${one(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}`,
    { method: 'GET', headers: headers(accessToken) },
  );
}

export async function deleteSnapshot(accessToken, dashboardId, snapshotId) {
  return fetchWithHandling(
    `${one(dashboardId)}/snapshots/${encodeURIComponent(snapshotId)}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } },
  );
}

/** Save a new spec. `version` is the one being edited; a stale one is a 409. */
export async function updateDashboard(accessToken, dashboardId, spec, version) {
  return fetchWithHandling(one(dashboardId), {
    method: 'PUT',
    headers: headers(accessToken),
    body: JSON.stringify({ spec, version }),
  });
}

export async function deleteDashboard(accessToken, dashboardId) {
  return fetchWithHandling(one(dashboardId), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

/** Undo history, newest first: { versions: [{ version, author, spec, created_at }] }. */
export async function listDashboardVersions(accessToken, dashboardId) {
  return fetchWithHandling(`${one(dashboardId)}/versions`, {
    method: 'GET',
    headers: headers(accessToken),
  });
}

/** Save `restoreVersion` again as a new version; `version` is the open one. */
export async function restoreDashboard(
  accessToken,
  dashboardId,
  restoreVersion,
  version,
) {
  return fetchWithHandling(`${one(dashboardId)}/restore`, {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({ restore_version: restoreVersion, version }),
  });
}

// --- Public links (owner only). The token comes back from create only. ---

export async function listShareLinks(accessToken, dashboardId) {
  return fetchWithHandling(`${one(dashboardId)}/share-links`, {
    method: 'GET',
    headers: headers(accessToken),
  });
}

/** { ...link, token }. expiresInDays null = until revoked; snapshotId =
 * show that saved snapshot instead of the data as it is now. */
export async function createShareLink(
  accessToken,
  dashboardId,
  expiresInDays,
  snapshotId = null,
) {
  return fetchWithHandling(`${one(dashboardId)}/share-links`, {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify({
      expires_in_days: expiresInDays || null,
      snapshot_id: snapshotId || null,
    }),
  });
}

/** Re-take the snapshot the link shows. */
export async function refreshShareLink(accessToken, dashboardId, linkId) {
  return fetchWithHandling(
    `${one(dashboardId)}/share-links/${encodeURIComponent(linkId)}/refresh`,
    { method: 'POST', headers: headers(accessToken) },
  );
}

export async function revokeShareLink(accessToken, dashboardId, linkId) {
  return fetchWithHandling(
    `${one(dashboardId)}/share-links/${encodeURIComponent(linkId)}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } },
  );
}

/** A shared snapshot, no login. Plain fetch: no cookies, no login redirect. */
export async function getPublicDashboard(token) {
  const response = await fetch(
    `${API_URL}/public/dashboards/${encodeURIComponent(token)}`,
    { credentials: 'omit' },
  );
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw body.error || { message: `Error ${response.status}` };
  return body;
}
